<template>
  <ClientOnly>
    <div
      v-if="recommendationProductIds && recommendationProductIds.ids.length"
      class="text-center"
      :data-testid="`monetate-carousel-${randomId}`"
    >
      <TsTypography
        as="h2"
        variant="h1"
        append-class="mb-2"
        align="center"
        :data-testid="`${props.title}-${randomId}`"
      >
        {{ props.title }}
      </TsTypography>
      <div class="lg:-mx-1.5" :monetate-id="props.componentName">
        <TsCarouselCardProduct
          product-context-type="recommended"
          :navigation="!isMobile"
          data-testid="product-others-carousel"
          :product-ids="recommendationProductIds.ids"
          @product-click="
            monetateStore.trackRecommendationClick(appRegion, recommendationProductIds, $event)
          "
        />
      </div>
    </div>
  </ClientOnly>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick } from 'vue';
import MonetateService from '~/services/monetate.service';
import type { ActionItems } from '~/types/monetate/monetate.type';

const TsCarouselCardProduct = defineAsyncComponent(
  () => import('~/components/organisms/TsCarouselCardProduct.vue')
);
const runtimeConfig = useRuntimeConfig();
const appRegion = runtimeConfig.public.appRegion;
const monetateStore = useMonetateStore();

type Props = {
  title?: string;
  pageType?: string;
  componentName?: string;
};

const { $customClientErrorHandler } = useNuxtApp();

const { isMobile } = useDevice();
const randomId = ref<string>('');

const recommendationProductIds = ref<ActionItems>();

const props = withDefaults(defineProps<Props>(), {
  title: 'Recently View',
  pageType: 'faq',
  componentName: 'recently-view',
});

// Local state specific to this component instance
const localPageType = ref(props.pageType);
const localComponentName = ref(props.componentName);

onMounted(async () => {
  nextTick(() => {
    randomId.value = useRandomUUID();
  });
});

// Fetch recommendations for the specific component instance
const fetchRecommendations = async () => {
  const monetate = new MonetateService();
  const event_object = [
    {
      eventType: 'monetate:context:PageView',
      url: appRegion === 'NL' ? 'https://www.toolstation.nl/' : 'https://www.toolstation.be/',
      pageType: localPageType.value, // Use local state
    },
  ];
  try {
    const response = await monetate.callEngineApi(appRegion, event_object);
    const action = monetate.filterActions(response, {
      actionType: 'monetate:action:OmnichannelRecommendation',
    });

    const my_builder_action = getActionItems(localComponentName.value, action);
    if (my_builder_action) {
      recommendationProductIds.value = my_builder_action; // Proper reactivity
    }
  } catch (e) {
    const errorMeta = { event_object, monetateError: true };

    $customClientErrorHandler(
      e,
      'high',
      'Monetate Error - trying to fetch recommended product for carousel using fetchRecommendations()',
      errorMeta
    );
    return e;
  }
};

// Watch for props changes and re-fetch recommendations
const stopFetchRecommendationWatcher = watch(
  () => [props.title, props.pageType, props.componentName],
  async () => {
    localPageType.value = props.pageType; // Sync with local state
    localComponentName.value = props.componentName; // Sync with local state
    await fetchRecommendations();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  stopFetchRecommendationWatcher();
});
</script>
